import { useCallback, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { displayEntity } from "common/api/entities";
import type { Entity } from "common/entities/types";
import { isSelectField } from "common/query/types";
import type { Context } from "common/types/context";
import type { SchedulerFilter } from "common/types/scheduler-filters";
import { Selector } from "common/widgets/selector";
import { Sizer, Sizes } from "common/widgets/sizer";
import {
  getWorkOrdersData,
  setDefaultPreferences,
} from "x/scheduler2/preferences";
import type { ViewType } from "x/scheduler2/types";
import { Configuration } from "./configuration";
import { ToggleableDayGroup } from "./day-group";
import {
  getCurrentDateRangeDays,
  getOverdueWOsRange,
  getParentPanelHeight,
  isDayOpened,
  type WorkOrdersDayRange,
} from "./functions";
import {
  defaultWOSelect,
  getDefaultFilter,
  getSearchQuery,
} from "./functions/query";
import type { OnWorkOrderViewClick } from "./types";

interface PropTypes {
  workOrderEntities: Entity[];
  context: Context;
  selectedDate: string;
  view: ViewType;
  onWorkOrderViewClick: OnWorkOrderViewClick;
  hideWeekends: boolean;
  isDraggable: boolean;
  isVisible: boolean;
}

export function WorkOrdersPanel({
  workOrderEntities,
  context,
  selectedDate,
  view,
  onWorkOrderViewClick,
  hideWeekends,
  isDraggable,
  isVisible,
}: Readonly<PropTypes>) {
  const [selectedWorkOrderEntity, setSelectedWorkOrderEntity] =
    useState<Entity>();

  const workOrderEntity = selectedWorkOrderEntity ?? workOrderEntities?.[0];

  const preferences = getWorkOrdersData(context);

  const [filterQuery, setFilterQuery] = useState<SchedulerFilter>(
    preferences?.filterQuery ?? getDefaultFilter(context, workOrderEntity),
  );
  const [searchValue, setSearchValue] = useState<string>(
    preferences?.searchValue,
  );

  const onConfigurationChange = (value: SchedulerFilter) => {
    setFilterQuery(value);
    setDefaultPreferences(context, "workOrdersFilter", value);
  };

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setDefaultPreferences(context, "defaultWOSearch", { value });
    },
    [context, setSearchValue],
  );

  const onEntityChange = (entity: Entity) => {
    unstable_batchedUpdates(() => {
      setSelectedWorkOrderEntity(entity);
      setFilterQuery(getDefaultFilter(context, entity));
      setSearchValue(undefined);
    });
  };

  const isDraggableEnabled = isDraggable && isVisible;
  const dayRanges = useMemo(
    () => [
      getOverdueWOsRange(view, workOrderEntity),
      ...getCurrentDateRangeDays(selectedDate, view, hideWeekends),
    ],
    [view, selectedDate, hideWeekends],
  );

  const customSelect =
    filterQuery?.query?.select.filter(isSelectField) || defaultWOSelect;
  const searchQuery = searchValue
    ? getSearchQuery(context, workOrderEntity, customSelect, searchValue)
    : undefined;

  const renderPanel = (sizes: Sizes) => {
    return (
      <>
        {dayRanges.map((dateRange: WorkOrdersDayRange) => (
          <ToggleableDayGroup
            key={dateRange.from ?? dateRange.label}
            label={dateRange.label}
            context={context}
            isDraggable={isDraggableEnabled}
            dateRange={dateRange}
            filterQuery={filterQuery}
            searchQuery={searchQuery}
            searchValue={searchValue}
            onWorkOrderViewClick={onWorkOrderViewClick}
            workOrderEntity={workOrderEntity}
            additionalInfo={dateRange.additionalInfo}
            isOpened={isDayOpened(view, dayRanges, dateRange)}
            parentPanelHeight={getParentPanelHeight(sizes)}
          />
        ))}
      </>
    );
  };

  if (!workOrderEntity) return null;

  return (
    <div className="x-scheduler-work-orders-panel">
      {workOrderEntities?.length > 1 && (
        <div data-testid="entity-selector">
          <Selector<Entity>
            className="x-padding-bottom-5"
            options={workOrderEntities}
            getOptionLabel={displayEntity}
            value={workOrderEntity}
            onChange={onEntityChange}
          />
        </div>
      )}
      <Configuration
        workOrderEntity={workOrderEntity}
        context={context}
        searchValue={searchValue}
        onSearchValueChange={onSearchChange}
        value={filterQuery}
        onChange={onConfigurationChange}
      />
      <Sizer className="x-scheduler-day-groups" render={renderPanel} />
    </div>
  );
}
